/**
 * ui.jsx — shared primitives.
 *
 * Notable fixes over the original:
 *   * alert() replaced with a real toast system. alert() blocks the main
 *     thread, cannot be styled, cannot be dismissed by keyboard on some
 *     platforms, and is silently suppressed in some in-app browsers — so
 *     "Link copied!" simply never appeared for some users.
 *   * Every modal now traps focus, restores it on close, closes on Escape, and
 *     locks body scroll. The original modals could be tabbed behind, and on iOS
 *     the page scrolled underneath them.
 *   * Skeletons everywhere something loads, instead of a blank area or a
 *     spinner that gives no sense of what is coming.
 *   * A RichText renderer that turns @mentions, #hashtags and URLs into real
 *     React elements. No dangerouslySetInnerHTML anywhere in this codebase.
 */

import {
  createContext, useContext, useState, useCallback, useEffect, useLayoutEffect, useRef, useId, useMemo, Component, forwardRef,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Check, X, AlertTriangle, Info, Loader2, ExternalLink } from 'lucide-react';
import { tokenize, displayUrl } from '../lib/safe';
import { initials, colourFor, timeAgo, fullDate, spokenAgo } from '../lib/format';

/* ══════════════════════════════════════════════════════════════════════════
   TOASTS
   ══════════════════════════════════════════════════════════════════════════ */

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const counter = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((message, { kind = 'info', duration = 5000, action } = {}) => {
    counter.current += 1;
    const id = counter.current;
    setToasts((list) => [...list.slice(-2), { id, message, kind, action }]);
    if (duration) setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  const api = useMemo(() => ({
    push,
    dismiss,
    success: (m, o) => push(m, { ...o, kind: 'success' }),
    error: (m, o) => push(m, { ...o, kind: 'error', duration: 8000 }),
    info: (m, o) => push(m, { ...o, kind: 'info' }),
  }), [push, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        // role="status" + aria-live="polite" means a screen reader announces
        // the toast without interrupting whatever the user is doing.
        <div className="toasts" role="status" aria-live="polite" aria-atomic="false">
          {toasts.map((t) => (
            <div key={t.id} className={`toast toast--${t.kind} anim-fade-in`}>
              <span className="toast__icon" aria-hidden="true">
                {t.kind === 'success' && <Check size={16} />}
                {t.kind === 'error' && <AlertTriangle size={16} />}
                {t.kind === 'info' && <Info size={16} />}
              </span>
              <span className="toast__message">{t.message}</span>
              {t.action && (
                <button type="button" className="toast__action" onClick={() => { t.action.onClick(); dismiss(t.id); }}>
                  {t.action.label}
                </button>
              )}
              <button
                type="button"
                className="toast__close"
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss notification"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>.');
  return ctx;
}

/* ══════════════════════════════════════════════════════════════════════════
   FOCUS TRAP + MODAL
   ══════════════════════════════════════════════════════════════════════════ */

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function useFocusTrap(active, containerRef, onEscape) {
  useEffect(() => {
    if (!active) return undefined;

    const previouslyFocused = document.activeElement;

    // Focus the first meaningful control, not the container, so keyboard users
    // land somewhere useful.
    const focusFirst = () => {
      const el = containerRef.current?.querySelector(FOCUSABLE);
      (el || containerRef.current)?.focus();
    };
    const raf = requestAnimationFrame(focusFirst);

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onEscape?.();
        return;
      }
      if (e.key !== 'Tab') return;

      const nodes = [...(containerRef.current?.querySelectorAll(FOCUSABLE) ?? [])]
        .filter((n) => n.offsetParent !== null);
      if (!nodes.length) return;

      const first = nodes[0];
      const last = nodes[nodes.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);

    const { overflow, paddingRight } = document.body.style;
    const barWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (barWidth > 0) document.body.style.paddingRight = `${barWidth}px`;

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = overflow;
      document.body.style.paddingRight = paddingRight;
      // Returning focus to whatever opened the modal is what keeps keyboard
      // navigation coherent — without it, focus resets to <body>.
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [active, containerRef, onEscape]);
}

export function Modal({ open, onClose, title, description, children, footer, size = 'md' }) {
  const ref = useRef(null);
  const titleId = useId();
  const descId = useId();
  useFocusTrap(open, ref, onClose);

  if (!open) return null;

  return createPortal(
    <div className="modal__backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={`modal modal--${size} anim-pop`}
      >
        <div className="modal__header">
          <h2 id={titleId} className="modal__title">{title}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close dialog">
            <X size={18} />
          </button>
        </div>
        {description && <p id={descId} className="modal__description">{description}</p>}
        <div className="modal__body">{children}</div>
        {footer && <div className="modal__footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

/**
 * Destructive confirmations.
 * `confirmWord` forces the user to type e.g. "DELETE" for irreversible actions —
 * used for account deletion, where an accidental click is unrecoverable.
 */
export function ConfirmDialog({
  open, onClose, onConfirm, title, body,
  confirmLabel = 'Confirm', danger = true, confirmWord,
}) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const inputId = useId();

  useEffect(() => { if (open) { setTyped(''); setBusy(false); } }, [open]);

  const canConfirm = !confirmWord || typed.trim().toUpperCase() === confirmWord.toUpperCase();

  const run = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onClose();
    } catch {
      // The action surfaces its own toast; keep the dialog open so the user can retry.
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            onClick={run}
            disabled={!canConfirm || busy}
            loading={busy}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-soft">{body}</p>
      {confirmWord && (
        <div style={{ marginTop: 16 }}>
          <label htmlFor={inputId} className="field__label">
            Type <strong>{confirmWord}</strong> to confirm
          </label>
          <input
            id={inputId}
            className="field__input"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      )}
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   BUTTONS
   ══════════════════════════════════════════════════════════════════════════ */

export function Button({
  variant = 'primary', size = 'md', loading, disabled,
  icon, children, className = '', ...rest
}) {
  return (
    <button
      type="button"
      className={`btn btn--${variant} btn--${size} ${className}`}
      disabled={disabled || loading}
      // Announces the busy state instead of the button just going quiet.
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading
        ? <Loader2 size={16} className="spin" aria-hidden="true" />
        : icon && <span aria-hidden="true" style={{ display: 'inline-flex' }}>{icon}</span>}
      {children}
    </button>
  );
}

/**
 * Icon-only button. `label` is mandatory — an icon button with no accessible
 * name is the single most common accessibility failure in social UIs, and the
 * original had a dozen of them.
 */
/**
 * forwardRef is not optional here.
 *
 * Menu positions itself against its trigger by holding a ref to it, and passes
 * that ref down as a prop. React silently drops `ref` on a plain function
 * component — so triggerRef.current stayed null, Menu's position() bailed out
 * early, coords was never set, and the menu simply never appeared.
 *
 * The symptom was a hamburger on every post that did nothing at all when
 * clicked: no error, no dropdown, no clue. Any component used as a Menu trigger
 * must forward its ref.
 */
export const IconButton = forwardRef(function IconButton(
  { label, icon, active, danger, className = '', ...rest },
  ref
) {
  if (!label && process.env.NODE_ENV !== 'production') {
    console.error('IconButton requires a `label` for screen readers.');
  }
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={active !== undefined ? active : undefined}
      className={`icon-btn${active ? ' is-active' : ''}${danger ? ' is-danger' : ''} ${className}`}
      {...rest}
    >
      {icon}
    </button>
  );
});

/* ══════════════════════════════════════════════════════════════════════════
   AVATAR
   ══════════════════════════════════════════════════════════════════════════ */

export function Avatar({ profile, size = 40, to, className = '' }) {
  const [failed, setFailed] = useState(false);

  const name = profile?.displayName || profile?.username || 'User';
  const colour = profile?.avatarColour || colourFor(profile?.uid || name);
  const src = !failed ? profile?.avatarUrl : null;

  const inner = src ? (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      className="avatar__img"
    />
  ) : (
    <span className="avatar__initials" style={{ fontSize: Math.round(size * 0.38) }}>
      {initials(name)}
    </span>
  );

  const style = { width: size, height: size, background: src ? 'transparent' : colour };

  // Decorative: the adjacent name is the accessible label, so an alt here would
  // make screen readers announce every name twice.
  if (to) {
    return (
      <Link to={to} className={`avatar ${className}`} style={style} aria-hidden="true" tabIndex={-1}>
        {inner}
      </Link>
    );
  }
  return <span className={`avatar ${className}`} style={style} aria-hidden="true">{inner}</span>;
}

/* ══════════════════════════════════════════════════════════════════════════
   RICH TEXT
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Renders post and comment bodies with live @mentions, #hashtags and links.
 *
 * Built from tokens, never from an HTML string — so there is no code path in
 * this app where user text can become markup. External links carry
 * rel="noopener noreferrer nofollow ugc": noopener/noreferrer close the
 * window.opener hijack, and nofollow/ugc stop your domain authority being
 * farmed by spammers posting links.
 */
export function RichText({ text, className = '' }) {
  const tokens = useMemo(() => tokenize(text), [text]);

  return (
    <span className={`rich-text wrap-anywhere ${className}`}>
      {tokens.map((token, i) => {
        switch (token.type) {
          case 'mention':
            return (
              <Link key={i} to={`/u/${token.value}`} className="rich-text__mention">
                @{token.value}
              </Link>
            );
          case 'hashtag':
            return (
              <Link key={i} to={`/tag/${token.value}`} className="rich-text__hashtag">
                #{token.value}
              </Link>
            );
          case 'link':
            return (
              <a
                key={i}
                href={token.href}
                target="_blank"
                rel="noopener noreferrer nofollow ugc"
                className="rich-text__link"
              >
                {displayUrl(token.href)}
                <ExternalLink size={11} aria-hidden="true" style={{ marginLeft: 2, verticalAlign: 'baseline' }} />
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            );
          default:
            return <span key={i}>{token.value}</span>;
        }
      })}
    </span>
  );
}

/** Relative time with the absolute date available to hover and to screen readers. */
export function TimeStamp({ value, className = '' }) {
  const rel = timeAgo(value);
  if (rel === null) return <span className={`time ${className}`}>Sending…</span>;
  return (
    <time className={`time ${className}`} title={fullDate(value)} aria-label={spokenAgo(value)}>
      {rel}
    </time>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   LOADING + EMPTY STATES
   ══════════════════════════════════════════════════════════════════════════ */

export function Skeleton({ width = '100%', height = 14, radius, style, className = '' }) {
  return (
    <span
      className={`skeleton ${className}`}
      style={{ display: 'block', width, height, borderRadius: radius, ...style }}
      aria-hidden="true"
    />
  );
}

/** Matches the real PostCard's geometry so the layout does not jump on load. */
export function PostSkeleton() {
  return (
    <article className="card" style={{ padding: 16 }} aria-hidden="true">
      <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
        <Skeleton width={40} height={40} radius="50%" />
        <div style={{ flex: 1 }}>
          <Skeleton width="42%" height={13} style={{ marginBottom: 7 }} />
          <Skeleton width="62%" height={11} />
        </div>
      </div>
      <Skeleton height={13} style={{ marginBottom: 7 }} />
      <Skeleton height={13} style={{ marginBottom: 7 }} />
      <Skeleton width="72%" height={13} style={{ marginBottom: 16 }} />
      <div style={{ display: 'flex', gap: 20 }}>
        <Skeleton width={54} height={16} />
        <Skeleton width={54} height={16} />
        <Skeleton width={54} height={16} />
      </div>
    </article>
  );
}

export function CardSkeleton() {
  return (
    <article className="card" style={{ padding: 20 }} aria-hidden="true">
      <Skeleton width="34%" height={11} style={{ marginBottom: 14 }} />
      <Skeleton height={20} style={{ marginBottom: 8 }} />
      <Skeleton width="82%" height={20} style={{ marginBottom: 18 }} />
      <Skeleton height={52} radius={8} style={{ marginBottom: 14 }} />
      <Skeleton height={12} style={{ marginBottom: 6 }} />
      <Skeleton width="90%" height={12} />
    </article>
  );
}

export function ConversationSkeleton() {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '12px 16px' }} aria-hidden="true">
      <Skeleton width={44} height={44} radius="50%" />
      <div style={{ flex: 1 }}>
        <Skeleton width="46%" height={13} style={{ marginBottom: 7 }} />
        <Skeleton width="76%" height={11} />
      </div>
    </div>
  );
}

/**
 * Loading announcement for assistive tech. A visual skeleton communicates
 * nothing to a screen reader, so every skeleton region pairs with this.
 */
export function LoadingRegion({ label = 'Loading', children }) {
  return (
    <div aria-busy="true" aria-live="polite">
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

export function EmptyState({ icon, title, body, action }) {
  return (
    <div className="empty">
      {icon && <div className="empty__icon" aria-hidden="true">{icon}</div>}
      <h3 className="empty__title">{title}</h3>
      {body && <p className="empty__body">{body}</p>}
      {action}
    </div>
  );
}

/**
 * Error state that always offers a way forward, never a dead end.
 *
 * The default title used to be "Something went wrong", which is the most
 * common sentence in software and tells nobody anything. Every call site names
 * what failed; this default exists only so a new one cannot ship without a
 * title at all, and it says the one thing that is always true — the failure is
 * on this side of the screen, not something the reader did.
 *
 * `body` is not decoration. An error with no second line leaves the reader to
 * guess whether to wait, retry, or give up, so every call site should say which.
 */
export function ErrorState({ title = 'That did not load', body, onRetry, retryLabel = 'Try again' }) {
  return (
    <div className="empty" role="alert">
      <div className="empty__icon" aria-hidden="true"><AlertTriangle size={28} /></div>
      <h3 className="empty__title">{title}</h3>
      {body && <p className="empty__body">{body}</p>}
      <div className="empty__actions">
        {onRetry && <Button variant="secondary" onClick={onRetry}>{retryLabel}</Button>}
        {/* Always reachable. A retry that keeps failing with no other route out
            is the point at which people assume the product is broken and leave
            rather than tell anyone. */}
        <Link to="/support" className="btn btn--ghost btn--sm">Report a problem</Link>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   FORM CONTROLS
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Text field with a real <label>, error wiring and hint text.
 *
 * The original used placeholders as labels. Placeholders disappear the moment
 * you type, so anyone who is interrupted loses the context of what a field
 * wanted — and they fail WCAG 3.3.2.
 */
export function Field({
  label, hint, error, id: providedId, as = 'input',
  maxLength, value, showCount, ...rest
}) {
  const autoId = useId();
  const id = providedId || autoId;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const Tag = as;

  const describedBy = [hint && hintId, error && errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="field">
      <div className="field__top">
        <label htmlFor={id} className="field__label">{label}</label>
        {showCount && maxLength && (
          <span
            className={`field__count${(value?.length ?? 0) > maxLength * 0.9 ? ' is-near-limit' : ''}`}
            aria-live="polite"
          >
            {value?.length ?? 0}/{maxLength}
          </span>
        )}
      </div>
      <Tag
        id={id}
        className={`field__input${error ? ' is-invalid' : ''}`}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy}
        maxLength={maxLength}
        value={value}
        {...rest}
      />
      {hint && !error && <p id={hintId} className="field__hint">{hint}</p>}
      {error && <p id={errorId} className="field__error" role="alert">{error}</p>}
    </div>
  );
}

export function Toggle({ checked, onChange, label, description, id: providedId, disabled = false }) {
  const autoId = useId();
  const id = providedId || autoId;
  const descId = `${id}-desc`;

  return (
    <div className="toggle-row">
      <div className="toggle-row__text">
        <label htmlFor={id} className="toggle-row__label">{label}</label>
        {description && <p id={descId} className="toggle-row__desc">{description}</p>}
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-describedby={description ? descId : undefined}
        disabled={disabled}
        onClick={() => { if (!disabled) onChange(!checked); }}
        className={`toggle${checked ? ' is-on' : ''}`}
      >
        <span className="toggle__knob" />
      </button>
    </div>
  );
}

/**
 * Tooltip that works on touch and keyboard, not only hover.
 *
 * The original used a hover-only span. On a phone that content was completely
 * unreachable — including the explanation of what the relevance score means,
 * which is the thing users most need explained.
 */
/**
 * Tooltip that works on touch and keyboard, not only hover.
 *
 * The bubble is measured after render and nudged horizontally so it can never
 * cross the viewport edge. Without that, a tip near the right-hand side of the
 * screen renders half off-page and — because it is a positioned element inside
 * normal flow — drags a horizontal scrollbar onto the whole document. That is
 * exactly what happened to the "let colleagues find me" tip in onboarding.
 *
 * Measuring in useLayoutEffect means the correction is applied before paint, so
 * the bubble never visibly jumps.
 */
export function InfoTip({ text, label = 'More information' }) {
  const [open, setOpen] = useState(false);
  const [shift, setShift] = useState(0);
  const id = useId();
  const ref = useRef(null);
  const bubbleRef = useRef(null);
  const hoverTimer = useRef(null);

  /**
   * Hover opens after a pause; click opens at once.
   *
   * Opening the instant the cursor touches the "?" means these bubbles fire
   * while someone is simply moving the mouse across a card, and a page that
   * pops explanatory boxes at you as you pass over it feels twitchy rather than
   * helpful. 600ms is long enough to mean "I stopped here" and short enough not
   * to feel broken.
   *
   * A click always wins, with no delay, which is also what makes this work on a
   * phone: touch fires no mouseenter, so the tap path is the only path there
   * and it must stay instant.
   */
  const HOVER_DELAY_MS = 600;

  const openAfterPause = () => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setOpen(true), HOVER_DELAY_MS);
  };

  const cancelPause = () => {
    clearTimeout(hoverTimer.current);
    setOpen(false);
  };

  // A pending timer that fires after unmount would set state on a component
  // that no longer exists, and worse, would leak one timer per tooltip on a
  // feed that renders dozens.
  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  useLayoutEffect(() => {
    if (!open || !bubbleRef.current) {
      setShift(0);
      return;
    }
    // Reset before measuring, or we compound the previous correction.
    bubbleRef.current.style.transform = 'translateX(-50%)';
    const rect = bubbleRef.current.getBoundingClientRect();
    const GUTTER = 10;

    let delta = 0;
    if (rect.right > window.innerWidth - GUTTER) {
      delta = -(rect.right - (window.innerWidth - GUTTER));
    } else if (rect.left < GUTTER) {
      delta = GUTTER - rect.left;
    }
    setShift(delta);
  }, [open, text]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  return (
    <span className="infotip" ref={ref}>
      <button
        type="button"
        className="infotip__trigger"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onClick={(e) => {
          e.preventDefault();
          // Cancel any pending hover timer, or the bubble reopens 600ms after
          // the tap that just closed it.
          clearTimeout(hoverTimer.current);
          setOpen((o) => !o);
        }}
        onMouseEnter={openAfterPause}
        onMouseLeave={cancelPause}
        // Keyboard focus is deliberate in a way that passing the mouse is not,
        // so it opens immediately.
        onFocus={() => setOpen(true)}
        onBlur={cancelPause}
      >
        ?
      </button>
      {open && (
        <span
          id={id}
          ref={bubbleRef}
          role="tooltip"
          className="infotip__bubble"
          style={{ transform: `translateX(calc(-50% + ${shift}px))` }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   ERROR BOUNDARY
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Without this, one thrown error in one component unmounts the entire React
 * tree and the user gets a blank white page with no explanation — the single
 * most damaging failure mode a SPA has.
 */
/**
 * A short, stable code for one kind of failure.
 *
 * Deterministic on the error's message and the first frame of its stack, so the
 * same bug always produces the same code. That is the whole point: when two
 * people report "LF-7K2QX9" it is one bug, and when someone reports it twice a
 * week apart it is still not fixed. A random id per occurrence would tell
 * nobody anything.
 */
function errorCode(error) {
  const frame = String(error?.stack || '').split('\n')[1]?.trim() || '';
  const seed = `${error?.name || 'Error'}:${error?.message || ''}:${frame}`;

  // FNV-1a, 32-bit. Not a hash for security — a hash for saying the same thing
  // twice.
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return `LF-${(h >>> 0).toString(36).toUpperCase().padStart(6, '0').slice(0, 6)}`;
}

/**
 * Catches a render error and shows something a person can act on.
 *
 * THREE THINGS THIS DOES THAT THE ORIGINAL DID NOT
 *
 * 1. It clears itself when the route changes.
 *    This boundary wraps the router, and React keeps a caught error in state
 *    until something resets it — so one page throwing meant EVERY subsequent
 *    page showed the error screen, for the rest of the session. The person's
 *    only escape was a full reload, which is exactly what the screen told them
 *    to do, which is how a bug on one page came to look like a broken app.
 *
 * 2. It stops recommending a reload that has already failed.
 *    A deterministic error survives reloading. Telling someone to reload, and
 *    then telling them again when the same screen comes back, is the part that
 *    makes software feel contemptuous. The second time, it says so and points
 *    at support instead.
 *
 * 3. It gives them a reference code.
 *    "Something went wrong" with nothing to quote is a dead end. The code is
 *    stable per bug (see errorCode), so it is worth quoting and worth
 *    receiving.
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, resetKey: props.resetKey, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  /**
   * Reset on navigation.
   *
   * Runs before render, so the new route renders normally rather than flashing
   * the error screen for a frame first.
   */
  static getDerivedStateFromProps(props, state) {
    if (props.resetKey !== state.resetKey) {
      return { error: null, resetKey: props.resetKey, copied: false };
    }
    return null;
  }

  componentDidCatch(error, info) {
    const code = errorCode(error);
    console.error(`Unhandled UI error [${code}]`, error, info);

    /**
     * Remembered across reloads so the screen can tell "first time" from "this
     * keeps happening". sessionStorage, not localStorage: an error that was
     * transient last Tuesday should not change what this session says.
     *
     * Wrapped because Safari in private mode throws on access, and an error
     * screen that throws while rendering is an infinite loop.
     */
    try {
      const key = `leeflet:err:${code}`;
      this.seenBefore = sessionStorage.getItem(key) === '1';
      sessionStorage.setItem(key, '1');
    } catch {
      this.seenBefore = false;
    }

    // Wire an error reporter here. Scrub the message first: React error text
    // can contain rendered props, which for this app may include DM content.
    this.props.onError?.(error, info, code);
  }

  render() {
    const { error, copied } = this.state;
    if (!error) return this.props.children;

    const code = errorCode(error);
    const repeated = this.seenBefore;

    const copyDetails = async () => {
      try {
        await navigator.clipboard.writeText(
          `Clincordant error ${code}\n${new Date().toISOString()}\n${window.location.pathname}\n${error?.message || error}`
        );
        this.setState({ copied: true });
      } catch {
        // Clipboard can be denied; the code is on screen to read out anyway.
      }
    };

    return (
      <div className="boundary" role="alert">
        <AlertTriangle size={30} aria-hidden="true" />

        <h1>{repeated ? 'This page keeps failing' : 'This page did not load'}</h1>

        <p>
          {repeated
            ? 'Reloading has not fixed it, so something is genuinely wrong at our end rather than with your connection. Nothing you have written has been lost.'
            : 'Something in this page failed while it was rendering. The rest of Clincordant is unaffected, and nothing you have written has been lost.'}
        </p>

        <p className="boundary__code">
          <span>Reference</span>
          <code>{code}</code>
          <button type="button" onClick={copyDetails}>
            {copied ? 'Copied' : 'Copy details'}
          </button>
        </p>

        <div className="boundary__actions">
          {!repeated && <Button onClick={() => window.location.reload()}>Reload the page</Button>}
          <Button
            variant={repeated ? 'primary' : 'secondary'}
            onClick={() => { window.location.href = '/'; }}
          >
            Go to your briefing
          </Button>
          <Link to="/support" className="btn btn--ghost btn--md">Tell us about it</Link>
        </div>

        {import.meta.env.DEV && (
          <pre className="boundary__detail">{String(error?.stack || error)}</pre>
        )}
      </div>
    );
  }
}
